import { ensureCoreMapImages } from "./coreMapImages.js";
import { scheduleAssetReconciliation } from "./mapStyleLifecycle.js";
import {
  ensureRuntimeStopLayers,
  removeVehicleLayers,
  removeScooterLayers,
  updateScooterSource,
  updateSelectedTripShape,
  updateSelectedVehicle,
  updateVehicleSource,
} from "./mapRuntimeLayers.js";

/**
 * Reconcile the main-map layer graph for a known-current style generation.
 * Provider DTO conversion stays in the app; this receives only GeoJSON and
 * region-specific stop/image adapters.
 */
export async function reconcileMainMapRuntime({
  map,
  revision,
  isCurrent,
  isDark,
  preferSatellite,
  favourites,
  stopLayerIds,
  stopSourceConfig,
  selection,
  vehiclePositions,
  showVehicles,
  scooterPositions,
  showScooters,
  selectedTripShape,
  ensureVehicleBadges,
  ensureScooterImages,
  requestReconcile,
  onError,
  syncStopSelection,
  syncSearchLocation,
}) {
  if (!map || !isCurrent(revision)) return false;
  await ensureCoreMapImages(map, { isDark, preferSatellite });
  if (!isCurrent(revision)) return false;
  await ensureRuntimeStopLayers({
    mapInstance: map,
    isDark,
    preferSatellite,
    favourites,
    stopLayerIds,
    stopSourceConfig,
  });
  if (!isCurrent(revision)) return false;
  syncStopSelection?.(selection);
  if (!isCurrent(revision)) return false;
  await syncSearchLocation?.();
  if (!isCurrent(revision)) return false;

  // MapLibre parses the vehicle symbol layer as soon as its source is added.
  // Its routeImageId values must therefore already be present; the missing
  // image resolver is recovery for a later style race, not the first-load
  // loading path.
  if (showVehicles) {
    await ensureVehicleBadges?.();
    if (!isCurrent(revision)) return false;
    updateVehicleSource(map, selection, vehiclePositions);
    updateSelectedVehicle(map, selection, vehiclePositions);
  } else {
    removeVehicleLayers(map);
  }
  updateSelectedTripShape(map, selectedTripShape);

  if (showScooters) {
    updateScooterSource(map, selection, scooterPositions);
    // Scooter symbols may need generated count badges, which wait for web
    // fonts. Keep the live layer responsive and reconcile it once the assets
    // are ready instead of making the source wait for that work.
    if (ensureScooterImages) {
      scheduleAssetReconciliation({
        prepareAssets: ensureScooterImages,
        revision,
        isCurrent,
        requestReconcile,
        onError,
      });
    }
  } else {
    removeScooterLayers(map);
  }

  return true;
}
